import api from './client';

// Invoices API
export const invoicesAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  updateStatus: (id, status) => api.put(`/invoices/${id}/status?status=${status}`),
  delete: (id) => api.delete(`/invoices/${id}`),
  syncChiefOfStaff: () => api.post('/receipts/sync-chief-of-staff'),
  pdfMatchBatch: (invoiceIds) => api.post('/invoices/pdf-match-batch', { invoice_ids: invoiceIds }),
  pdfRenderableBatch: (invoiceIds) => api.post('/invoices/pdf-renderable-batch', { invoice_ids: invoiceIds }),
  fetchPdf: (invoiceId) => api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob', timeout: 30000 }),
  fetchAttachment: (invoiceId) => api.get(`/invoices/${invoiceId}/attachment`, { responseType: 'blob', timeout: 30000 }),
};

// Invoice taxonomy: chip-row counts, "Set category" row action, backfill.
// Preview/Apply backfill are budget-boxed chains (same engine as
// gmailAmountReextraction* below) -- each run time-boxes itself and
// schedules its own successor from the persisted cursor/tallies until the
// ~12,485-doc cohort converges, instead of one synchronous request handler
// call that 504s at the edge.
export const invoiceTaxonomyAPI = {
  getCounts: (params) => api.get('/invoice-taxonomy/counts', { params }),
  setCategory: (data) => api.post('/invoice-taxonomy/set-category', data),
  previewBackfillChainStart: () => api.post('/invoice-taxonomy/preview-backfill/chain-start'),
  previewBackfillChainStatus: () => api.get('/invoice-taxonomy/preview-backfill/chain-status'),
  previewBackfillChainStop: (chainId) => api.post('/invoice-taxonomy/preview-backfill/chain-stop', { chain_id: chainId }),
  applyBackfillChainStart: () => api.post('/invoice-taxonomy/apply-backfill/chain-start'),
  applyBackfillChainStatus: () => api.get('/invoice-taxonomy/apply-backfill/chain-status'),
  applyBackfillChainStop: (chainId) => api.post('/invoice-taxonomy/apply-backfill/chain-stop', { chain_id: chainId }),
  backfillJobStatus: (jobId) => api.get(`/invoice-taxonomy/backfill/job-status/${jobId}`),
  backfillJobLatest: (operation) => api.get(`/invoice-taxonomy/backfill/job-latest/${operation}`),
};

// Wise reference-mapping backfill: re-derives the payer-entered reference
// for Wise transactions synced before routes/wise_integration.py's fix.
// No raw payload stored for Wise, so every candidate is a live re-fetch
// (services.wise.get_transfer_fee) -- same budget-boxed chain shape as
// invoiceTaxonomyAPI above.
export const wiseReferenceBackfillAPI = {
  previewChainStart: () => api.post('/admin/wise-reference-backfill/preview/chain-start'),
  previewChainStatus: () => api.get('/admin/wise-reference-backfill/preview/chain-status'),
  previewChainStop: (chainId) => api.post('/admin/wise-reference-backfill/preview/chain-stop', { chain_id: chainId }),
  applyChainStart: () => api.post('/admin/wise-reference-backfill/apply/chain-start'),
  applyChainStatus: () => api.get('/admin/wise-reference-backfill/apply/chain-status'),
  applyChainStop: (chainId) => api.post('/admin/wise-reference-backfill/apply/chain-stop', { chain_id: chainId }),
  jobStatus: (jobId) => api.get(`/admin/wise-reference-backfill/job-status/${jobId}`),
  jobLatest: (operation) => api.get(`/admin/wise-reference-backfill/job-latest/${operation}`),
};

// Invoice country filter: chip-row counts for UK / France / Unassigned,
// derived server-side from each doc's entity (config.py ENTITY_DEFS).
export const invoiceCountryAPI = {
  getCounts: (params) => api.get('/invoice-country/counts', { params }),
};

// Invoice Financing (Paragon/Dancerace facility)
export const invoiceFinancingAPI = {
  getData: (params) => api.get('/costs/invoice-financing', { params }),
  getConfig: () => api.get('/costs/invoice-financing/config'),
  updateConfig: (data) => api.put('/costs/invoice-financing/config', data),
  getOverrides: () => api.get('/costs/invoice-financing/overrides'),
  createOverride: (data) => api.post('/costs/invoice-financing/overrides', data),
  deleteOverride: (id) => api.delete(`/costs/invoice-financing/overrides/${id}`),
};

// Supplier Invoice Dedup (Diagnostics card, Prompt AE): Gmail is
// canonical, a Xero-sourced supplier invoice duplicate merges INTO it
// (services/supplier_invoice_dedup.py). Preview never mutates; Apply
// defaults to the high_confidence tier only, includeMedium=true also
// merges medium_confidence after eyeballing the Preview rows. Ambiguous
// (fan-out) pairs are never auto-merged by either.
export const supplierInvoiceDedupAPI = {
  preview: () => api.post('/admin/diagnostics/supplier-invoice-dedup', { dry_run: true }),
  apply: (includeMedium = false) =>
    api.post('/admin/diagnostics/supplier-invoice-dedup', { dry_run: false, include_medium: includeMedium }),
  // Gmail-gmail dedup: two gmail-sourced docs for the same real invoice
  // (the bulk-accountant-forward class). Own endpoint -- no includeMedium,
  // Apply is high_confidence only, always.
  previewGmailGmail: () => api.post('/admin/diagnostics/supplier-invoice-dedup/gmail-gmail', { dry_run: true }),
  applyGmailGmail: () => api.post('/admin/diagnostics/supplier-invoice-dedup/gmail-gmail', { dry_run: false }),
};

// Retroactive attachment_sha256 backfill for gmail invoice docs that
// predate the go-forward ingestion hook -- feeds supplierInvoiceDedupAPI's
// gmail-gmail attachment-hash evidence tier. Budget-boxed chain, same
// start/status/stop shape as wiseReferenceBackfillAPI above (no
// Preview/Apply split -- computing and writing the hash IS the operation).
export const gmailGmailHashBackfillAPI = {
  start: () => api.post('/admin/diagnostics/gmail-gmail-hash-backfill/start'),
  status: () => api.get('/admin/diagnostics/gmail-gmail-hash-backfill/status'),
  stop: (chainId) => api.post('/admin/diagnostics/gmail-gmail-hash-backfill/stop', { chain_id: chainId }),
  jobStatus: (jobId) => api.get(`/admin/diagnostics/gmail-gmail-hash-backfill/job-status/${jobId}`),
  jobLatest: () => api.get('/admin/diagnostics/gmail-gmail-hash-backfill/job-latest'),
};

// Receivables Invoice Link: census (read-only, Phase 1) + repair (gated
// write, Phase 2) for Pennylane invoices created manually (no
// "{platform}:{booking_id}" external_reference), which the exact
// receivables dedup join can never see. census/preview return the same
// heuristic candidate pairing; resolve writes the missing reference for
// exactly one operator-approved (invoice, booking) pair.
export const receivablesInvoiceLinkAPI = {
  census: () => api.get('/admin/diagnostics/receivables-invoice-link-census', { timeout: 30000 }),
  preview: () => api.get('/admin/receivables-invoice-link/preview', { timeout: 30000 }),
  resolve: (invoiceId, platform, bookingId) => api.post(
    '/admin/receivables-invoice-link/resolve',
    { invoice_id: invoiceId, platform, booking_id: bookingId },
    { timeout: 30000 },
  ),
};
